import { Box, Flex } from '@sequencehq/core-components'
import { memo, useEffect, useRef } from 'react'
import type { Message, StatusType } from '../ai/useAiChat'
import { MessageBubble } from './MessageBubble'
import { StatusIndicator } from './StatusIndicator'
// removed new-reply button per UX request

interface Props {
  messages: Message[]
  currentStatus: StatusType
  statusMessage: string
}

export const ChatMessages = memo(
  ({ messages, currentStatus, statusMessage }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const lastMessageRef = useRef<HTMLDivElement>(null)
    const seenKeysRef = useRef<Set<string>>(new Set())

    // Reset dedupe state when conversation resets
    useEffect(() => {
      if (messages.length === 0) {
        seenKeysRef.current.clear()
      }
    }, [messages.length])

    // Ensure per-render dedupe is computed from scratch so earlier message
    // components don't disappear on subsequent renders.
    seenKeysRef.current.clear()

    const compKey = (c: any): string => {
      if (!c || typeof c !== 'object') return 'x'
      const t = c.type
      // Normalize IDs for cards
      if (t === 'invoice-card') {
        const id = c.invoiceId || c.id || ''
        return `${t}|${id}`
      }
      if (t === 'schedule-card') {
        const id = c.scheduleId || c.id || ''
        return `${t}|${id}`
      }
      if (t === 'product-card') {
        const id = c.productId || c.id || ''
        return `${t}|${id}`
      }
      if (t === 'metric') {
        return `${t}|${c.title ?? ''}|${c.value ?? ''}`
      }
      if (t === 'kpi-dashboard') {
        const metrics = Array.isArray(c.metrics) ? c.metrics : []
        const sig = metrics
          .map((m: any) => `${m.label ?? ''}:${m.value ?? ''}`)
          .join('|')
        return `${t}|${c.title ?? ''}|${sig}`
      }
      if (t === 'comparison') {
        return `${t}|${c.title ?? ''}|${c.current?.value ?? ''}|${c.previous?.value ?? ''}`
      }
      if (t === 'progress') {
        return `${t}|${c.title ?? ''}|${c.current ?? ''}|${c.target ?? ''}`
      }
      if (t === 'insight') {
        return `${t}|${c.title ?? ''}|${c.message ?? ''}`
      }
      if (t === 'pie') {
        const data = Array.isArray(c.data) ? c.data : []
        const sig = data
          .slice()
          .sort((a: any, b: any) =>
            (a.label || '').localeCompare(b.label || '')
          )
          .map(
            (d: any) =>
              `${d.label ?? ''}:${Math.round((d.value ?? 0) * 100) / 100}`
          )
          .join('|')
        return `${t}|${sig}`
      }
      if (t === 'horizontal-bar') {
        const data = Array.isArray(c.data) ? c.data : []
        const sig = data
          .slice()
          .sort((a: any, b: any) =>
            (a.label || '').localeCompare(b.label || '')
          )
          .map(
            (d: any) =>
              `${d.label ?? ''}:${Math.round((d.value ?? 0) * 100) / 100}`
          )
          .join('|')
        return `${t}|${sig}`
      }
      if (t === 'table') {
        const headers = Array.isArray(c.headers) ? c.headers.join('|') : ''
        const rows = Array.isArray(c.rows)
          ? c.rows
              .map((r: any) => (Array.isArray(r) ? r.join('~') : ''))
              .join('|')
          : ''
        return `${t}|${headers}|${rows}`
      }
      if (t === 'chart') {
        const data = Array.isArray(c.data) ? c.data : []
        const normalized = data
          .slice()
          .map((d: any) => ({
            date: new Date(d.date || d.label || '').toISOString(),
            value: Math.round((d.value ?? 0) * 100) / 100
          }))
          .sort((a: any, b: any) => (a.date || '').localeCompare(b.date || ''))
          .map((d: any) => `${d.date}:${d.value}`)
          .join('|')
        return `${t}|${normalized}`
      }
      // Fallback signature
      return `${t}|${c.title ?? ''}`
    }

    // Bring the latest message into view without snapping to absolute bottom
    useEffect(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ block: 'nearest' })
      }
    }, [messages.length])

    return (
      <Box position="relative" width="100%" height="100%">
        <Flex
          ref={containerRef}
          direction="column"
          gap={2}
          align="flex-start"
          justify="flex-start"
          overflow="auto"
          px={2}
          py={0}
          position="absolute"
          inset={0}
          width="100%"
          height="100%"
        >
          {messages.map((message, idx) => {
            // Check if message has components (single or multiple)
            if (
              message.component ||
              (message.components && message.components.length > 0)
            ) {
              // Dedupe card components across the entire chat session
              const toArray = (m: typeof message) =>
                m.components && m.components.length > 0
                  ? m.components
                  : m.component
                    ? [m.component]
                    : []

              const comps = toArray(message)
              const filtered = comps.filter(c => {
                if (!c || typeof c !== 'object') return false
                const key = compKey(c)
                if (seenKeysRef.current.has(key)) {
                  return false
                }
                seenKeysRef.current.add(key)
                return true
              })

              return (
                <Box
                  key={message.id}
                  ref={idx === messages.length - 1 ? lastMessageRef : undefined}
                  width="100%"
                >
                  <MessageBubble
                    author={message.role === 'user' ? 'user' : 'assistant'}
                    type="component"
                    component={undefined}
                    components={filtered}
                    textContent={message.content}
                  />
                </Box>
              )
            }

            // Regular text message
            return (
              <Box
                key={message.id}
                ref={idx === messages.length - 1 ? lastMessageRef : undefined}
                width="100%"
              >
                <MessageBubble
                  author={message.role === 'user' ? 'user' : 'assistant'}
                  type="text"
                >
                  {message.content}
                </MessageBubble>
              </Box>
            )
          })}

          {/* Show status indicator when processing */}
          {currentStatus !== 'idle' && (
            <Box width="100%" mb={2}>
              <StatusIndicator type={currentStatus} message={statusMessage} />
            </Box>
          )}
        </Flex>

        {/* No auto-scroll or new reply button per UX */}
      </Box>
    )
  }
)
